import { opponent, sameSquare } from '../board';
import type { MatchEvent, MatchState, Pawn, Side, Square } from '../types';
import type { HorrorId } from './ids';
import { luckBelow } from './flow';
import { hasRule, houseRule, L, markOnce, setStatus, untilOwnersNextTurn } from './state';

/**
 * The bosses' powers after Night III (LEDGER.MD §13.1): things the boss does to the table itself,
 * at the start of its own turns. Each is a House Rule played against South (the boss is the other
 * side), and each emits one Horror event naming every pawn it moved, so the table can show the
 * hands doing it.
 */

type Events = MatchEvent[];
type Move = { readonly pawnId: string; readonly from: Square; readonly to: Square };

/** Blind Eye: the half of the board in the dark switches after this many turns of a round. */
export const BLIND_SWITCH_TURN = 6;

const onBoardOf = (s: MatchState, side: Side): Pawn[] =>
  Object.values(s.pawns).filter((p) => p.side === side && p.status === 'onBoard' && !!p.square);

/** How far a pawn has come from its own home row (0 on it, 5 on the far one). */
const advance = (p: Pawn): number => (p.side === 'S' ? p.square!.y : 5 - p.square!.y);

const occupied = (s: MatchState, sq: Square): boolean =>
  Object.values(s.pawns).some(
    (p) => p.status === 'onBoard' && p.square && sameSquare(p.square, sq),
  );

const onTheBoard = (sq: Square): boolean => sq.x >= 0 && sq.x <= 5 && sq.y >= 0 && sq.y <= 5;

/** The pawn of `side` furthest from its home row; ties broken with the luck stream. */
function furthest(s: MatchState, side: Side, least = false): Pawn | undefined {
  const pawns = onBoardOf(s, side);
  if (pawns.length === 0) return undefined;
  const best = least ? Math.min(...pawns.map(advance)) : Math.max(...pawns.map(advance));
  const tied = pawns.filter((p) => advance(p) === best).sort((a, b) => a.id.localeCompare(b.id));
  return tied.length === 1 ? tied[0] : tied[luckBelow(s, tied.length)];
}

/** Moves pawns all at once (a bijection: no square ever holds two), and says so. */
function movePawns(s: MatchState, moves: readonly Move[]): void {
  for (const m of moves) {
    const p = s.pawns[m.pawnId];
    if (p) s.pawns[m.pawnId] = { ...p, square: m.to };
  }
}

/** The side a horror is played against (South unless the rule names another). */
const victimOf = (s: MatchState, id: HorrorId): Side => houseRule(s, id)?.side ?? 'S';

/** The half of the board in the dark under Blind Eye, as files (0 = A); null when none is. */
export function darkFiles(
  s: Pick<MatchState, 'config' | 'round' | 'turn'>,
): readonly number[] | null {
  if (!hasRule(s, 'blind-eye')) return null;
  const leftFirst = s.round % 2 === 0;
  const left = s.turn <= BLIND_SWITCH_TURN ? leftFirst : !leftFirst;
  return left ? [0, 1, 2] : [3, 4, 5];
}

/** Whether a square is in the dark under Blind Eye. */
export function inTheDark(s: Pick<MatchState, 'config' | 'round' | 'turn'>, sq: Square): boolean {
  return darkFiles(s)?.includes(sq.x) ?? false;
}

/**
 * At the start of every turn: Blind Eye's dark half falls (the round's first turn) and moves
 * (after BLIND_SWITCH_TURN turns); and at the boss's own turns, its power.
 */
export function horrorsAtTurnStart(s: MatchState, side: Side, ev: Events): void {
  if (!s.ledger) return;
  if (hasRule(s, 'blind-eye') && (s.turn === 1 || s.turn === BLIND_SWITCH_TURN + 1)) {
    const left = darkFiles(s)?.[0] === 0;
    ev.push({
      type: 'Horror',
      horror: 'blind-eye',
      side: opponent(victimOf(s, 'blind-eye')),
      moves: [],
      dark: left ? 'left' : 'right',
    });
  }
  const turns = L(s).round.turns[side];
  for (const r of s.config.ledger?.houseRules ?? []) {
    const boss = opponent(r.side ?? 'S');
    if (boss !== side) continue;
    switch (r.id) {
      case 'storm':
        if (turns >= 2 && turns % 2 === 0) storm(s, boss, ev);
        break;
      case 'turned-table':
        if (turns === 3) turnTheTable(s, boss, ev);
        break;
      case 'switch':
        if (turns === 2 || turns === 5) switchPawns(s, boss, ev);
        break;
      case 'lights-out':
        if (turns === 3) lightsOut(s, boss, ev);
        break;
      case 'clean-slate':
        cleanSlate(s, boss, ev);
        break;
      default:
        break;
    }
  }
}

/**
 * The Storm: lightning strikes the victim's pawn that has come furthest. It is knocked back a
 * square toward its home row, when that square is free, and Pinned until the end of its owner's
 * next turn.
 */
function storm(s: MatchState, boss: Side, ev: Events): void {
  const victim = opponent(boss);
  const p = furthest(s, victim);
  if (!p || !p.square) return;
  const back = { x: p.square.x, y: p.square.y + (victim === 'S' ? -1 : 1) };
  const moves: Move[] =
    onTheBoard(back) && !occupied(s, back) ? [{ pawnId: p.id, from: p.square, to: back }] : [];
  movePawns(s, moves);
  ev.push({ type: 'Horror', horror: 'storm', side: boss, moves, pawnId: p.id, at: p.square });
  setStatus(s, p.id, { kind: 'pinned', turns: untilOwnersNextTurn(s, victim) }, ev);
}

/**
 * The Turned Table: the boss turns the board half round, once a round. Every pawn ends on the
 * square opposite (A1 ↔ F6): each side's pawns stand where the other side's stood.
 */
function turnTheTable(s: MatchState, boss: Side, ev: Events): void {
  const moves: Move[] = Object.values(s.pawns)
    .filter((p) => p.status === 'onBoard' && p.square)
    .map((p) => ({
      pawnId: p.id,
      from: p.square!,
      to: { x: 5 - p.square!.x, y: 5 - p.square!.y },
    }));
  movePawns(s, moves);
  ev.push({ type: 'Horror', horror: 'turned-table', side: boss, moves });
}

/** The Switch: the victim's pawn that has come furthest trades places with the boss's hindmost. */
function switchPawns(s: MatchState, boss: Side, ev: Events): void {
  const mine = furthest(s, opponent(boss));
  const his = furthest(s, boss, true);
  if (!mine?.square || !his?.square) return;
  const moves: Move[] = [
    { pawnId: mine.id, from: mine.square, to: his.square },
    { pawnId: his.id, from: his.square, to: mine.square },
  ];
  movePawns(s, moves);
  ev.push({ type: 'Horror', horror: 'switch', side: boss, moves });
}

/**
 * Lights Out: once a round the lamp goes out, and in the dark the boss's furthest pawn steals a
 * step forward (straight, else diagonally, onto a free square) and the victim's furthest pawn is
 * nudged a square sideways (onto a free one).
 */
function lightsOut(s: MatchState, boss: Side, ev: Events): void {
  const moves: Move[] = [];
  const fwd = boss === 'S' ? 1 : -1;
  // His furthest pawn that has somewhere to step: straight ahead, else diagonally ahead.
  const his = onBoardOf(s, boss).sort(
    (a, b) => advance(b) - advance(a) || a.id.localeCompare(b.id),
  );
  for (const p of his) {
    const sq = p.square!;
    const to = [
      { x: sq.x, y: sq.y + fwd },
      { x: sq.x - 1, y: sq.y + fwd },
      { x: sq.x + 1, y: sq.y + fwd },
    ].find((t) => onTheBoard(t) && !occupied(s, t));
    if (to) {
      moves.push({ pawnId: p.id, from: sq, to });
      break;
    }
  }
  movePawns(s, moves);
  // The victim's furthest pawn that can be nudged aside onto a free square.
  const mine = onBoardOf(s, opponent(boss)).sort(
    (a, b) => advance(b) - advance(a) || a.id.localeCompare(b.id),
  );
  for (const p of mine) {
    const sq = p.square!;
    const sides = [
      { x: sq.x - 1, y: sq.y },
      { x: sq.x + 1, y: sq.y },
    ].filter((t) => onTheBoard(t) && !occupied(s, t));
    if (sides.length === 0) continue;
    const m = { pawnId: p.id, from: sq, to: sides[sides.length === 1 ? 0 : luckBelow(s, 2)]! };
    movePawns(s, [m]);
    moves.push(m);
    break;
  }
  ev.push({ type: 'Horror', horror: 'lights-out', side: boss, moves });
}

/**
 * Clean Slate: once a round, when a pawn of the victim's stands a step from crowning, the boss's
 * hands sweep the board: every pawn goes back to the square it started the match on (one whose
 * square is taken stays where it is).
 */
function cleanSlate(s: MatchState, boss: Side, ev: Events): void {
  const victim = opponent(boss);
  if (L(s).round.once.includes('clean-slate')) return;
  if (!onBoardOf(s, victim).some((p) => advance(p) >= 4)) return;
  markOnce(s, 'clean-slate');
  const pawns = Object.values(s.pawns).filter((p) => p.status === 'onBoard' && p.square);
  const homes = new Map<string, Square>();
  for (const p of pawns) {
    const home = p.startSquare;
    const clash = pawns.some((q) => q.id !== p.id && sameSquare(q.startSquare, home));
    homes.set(p.id, clash ? p.square! : home);
  }
  // Only moves that keep every square to one pawn: a pawn whose home holds a pawn staying put stays.
  const staying = new Set(
    pawns.filter((p) => sameSquare(homes.get(p.id)!, p.square!)).map((p) => p.id),
  );
  let changed = true;
  while (changed) {
    changed = false;
    for (const p of pawns) {
      if (staying.has(p.id)) continue;
      const home = homes.get(p.id)!;
      if (pawns.some((q) => staying.has(q.id) && sameSquare(q.square!, home))) {
        staying.add(p.id);
        changed = true;
      }
    }
  }
  const moves: Move[] = pawns
    .filter((p) => !staying.has(p.id))
    .map((p) => ({ pawnId: p.id, from: p.square!, to: homes.get(p.id)! }));
  movePawns(s, moves);
  ev.push({ type: 'Horror', horror: 'clean-slate', side: boss, moves });
}
